// ═══════════════════════════════════════════════════════════
// License Hooks — TanStack Query wrappers for OIA license API
// ═══════════════════════════════════════════════════════════
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth.store";
import type { LicenseCheckResult, OIAPlan } from "@shared";
import {
  TRIAL_FEATURES,
  FREE_FEATURES,
  SALES_BOOSTER_FEATURES,
  ENTERPRISE_FEATURES,
} from "@shared";
import { queryKeys } from "@/lib/query-keys";

// ─── Dev-only plan override ───────────────────────────────
// Active only in Vite DEV mode (import.meta.env.DEV = compile-time constant).
// Tree-shaken entirely from production builds — zero footprint in prod.
// localStorage key: "cmn_dev_plan_override" → "free" | "trial" | "sales_booster" | "enterprise"
export const DEV_OVERRIDE_KEY = "cmn_dev_plan_override";

const DEV_FEATURES_MAP = {
  free: FREE_FEATURES,
  trial: TRIAL_FEATURES,
  sales_booster: SALES_BOOSTER_FEATURES,
  enterprise: ENTERPRISE_FEATURES,
} as const;
export type DevPlanKey = keyof typeof DEV_FEATURES_MAP;

function applyDevPlanOverride(data: LicenseCheckResult): LicenseCheckResult {
  if (!import.meta.env.DEV) return data;
  const raw = localStorage.getItem(DEV_OVERRIDE_KEY);
  const override = raw as DevPlanKey | null;
  if (!override || !(override in DEV_FEATURES_MAP)) return data;
  return {
    ...data,
    features: DEV_FEATURES_MAP[override],
    planSlug: override === "trial" ? null : override,
  };
}

/** Fix JSON serialization of Infinity which becomes null over the wire */
function restoreInfinity(data: LicenseCheckResult): LicenseCheckResult {
  if (!data?.features) return data;
  const f = { ...data.features };
  if (f.maxRules === null) f.maxRules = Infinity;
  if (f.maxReps === null) f.maxReps = Infinity;
  if (f.historyRetentionMonths === null) f.historyRetentionMonths = Infinity;
  return { ...data, features: f };
}

function selectLicenseData(data: LicenseCheckResult): LicenseCheckResult {
  return applyDevPlanOverride(restoreInfinity(data));
}

/**
 * Fetches the current license status from the backend (OIA-backed, cached 5 min).
 * Only enabled once memberId is known (tenant is authenticated).
 * In DEV mode, the `select` transform intercepts the result and applies any
 * plan override stored in localStorage — letting admins preview gating behaviour
 * without touching real OIA data.
 */
export function useLicenseStatus() {
  const memberId = useAuthStore((s) => s.memberId);
  return useQuery({
    queryKey: queryKeys.license.status,
    queryFn: () => api.get<LicenseCheckResult>("/api/admin/license"),
    enabled: !!memberId,
    staleTime: 5 * 60_000,
    retry: 1,
    select: selectLicenseData,
  });
}

/**
 * Force-refresh license from OIA (admin action).
 */
export function useRefreshLicense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<LicenseCheckResult>("/api/admin/license/refresh", {}),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.license.status, data);
    },
  });
}

/**
 * Plans from the License Server via our backend (5-min server cache). Open to every tenant
 * user — the showcase must be visible to trial, expired and non-admin customers.
 */
export function useLicensePlans() {
  const memberId = useAuthStore((s) => s.memberId);
  return useQuery({
    queryKey: queryKeys.license.plans,
    queryFn: () => api.get<OIAPlan[]>("/api/admin/license/plans"),
    enabled: !!memberId,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export interface PlanUsage {
  rules: { used: number; limit: number | null };
  reps: { used: number; limit: number | null };
}

/** How much of the plan's limits this tenant uses (limit `null` = unlimited). */
export function useLicenseUsage() {
  const memberId = useAuthStore((s) => s.memberId);
  return useQuery({
    queryKey: queryKeys.license.usage,
    queryFn: () => api.get<PlanUsage>("/api/admin/license/usage"),
    enabled: !!memberId,
    staleTime: 60_000,
  });
}
